import { useState, useEffect, useMemo, useCallback } from 'react';
import { iAmHigherAuthority } from '../../../../libs.jsx';
import { getRoleWeight } from '../../../../services/session.js';
import { rolesForCampus } from '../../../../utils/palcareRoles.js';

const TITLE_OPTIONS = ['', 'Caregiver', 'Executive Director', 'Maintenance Director', 'Wellness Director',
  'Director of Nursing', 'Nurse', 'Other'];

/**
 * ApproveAddUserDialog — approval dialog for a staged "Add User" task.
 *
 * Mirrors UserDrawer Add mode: most fields are pre-filled from the staged
 * record (display name, email, phone). The admin must supply username,
 * password, role, and groups. Title is matched against TITLE_OPTIONS;
 * unrecognized values fall through to "Other" + custom field.
 *
 * @param {{
 *   task: object | null,         // staged ext_stg_user row
 *   roles: Array,
 *   campusId: number,            // the community the user is created on
 *   userGroups: Array,
 *   onApprove: (formData: object) => void,
 *   onReject: () => void,
 *   onClose: () => void,
 *   submitting: boolean,
 * }} props
 */
const ApproveAddUserDialog = ({ task, roles, campusId, userGroups, onApprove, onReject, onClose, submitting }) => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [roleId, setRoleId] = useState('');
  const [title, setTitle] = useState('');
  const [titleCustom, setTitleCustom] = useState('');
  const [selectedGroupIds, setSelectedGroupIds] = useState([]);
  const [groupSearch, setGroupSearch] = useState('');

  // Initialize / reset when task changes
  useEffect(() => {
    if (!task) return;
    setUsername(task.username || '');
    setPassword('');
    setConfirmPassword('');
    setRoleId(task.user_role_id ? String(task.user_role_id) : '');
    const t = task.ehr_job_title || '';
    if (TITLE_OPTIONS.includes(t)) {
      setTitle(t);
      setTitleCustom('');
    } else if (t) {
      setTitle('Other');
      setTitleCustom(t);
    } else {
      setTitle('');
      setTitleCustom('');
    }
    setSelectedGroupIds([]);
    setGroupSearch('');
  }, [task]);

  // Only roles the current user has authority over (matches UserDrawer behavior), and
  // PalCare roles only on a PalCare community (utils/palcareRoles.js).
  const myRoleWeight = getRoleWeight();
  const filteredRoles = useMemo(
    () => rolesForCampus(roles, campusId).filter((r) => iAmHigherAuthority(r.user_role_weight, myRoleWeight)),
    [roles, campusId, myRoleWeight]
  );

  const selectedRole = useMemo(
    () => roles?.find((r) => String(r.user_role_id) === roleId) || null,
    [roles, roleId]
  );
  const isCaregiver = selectedRole?.user_role_name?.toLowerCase() === 'caregiver';

  const isAdminWeakPassword = useMemo(() => {
    const rid = parseInt(roleId, 10);
    if (rid !== 1 && rid !== 2) return false;
    return password === '123456';
  }, [roleId, password]);

  const filteredGroups = useMemo(() => {
    let list = userGroups || [];
    if (groupSearch.trim()) {
      const s = groupSearch.toLowerCase();
      list = list.filter(
        (g) =>
          g.group_name?.toLowerCase().includes(s) ||
          g.capcode?.toLowerCase().includes(s)
      );
    }
    return list;
  }, [userGroups, groupSearch]);

  const handleGroupToggle = useCallback((gid) => {
    const id = String(gid);
    setSelectedGroupIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  }, []);

  const isFormValid = useMemo(() => {
    if (!username.trim()) return false;
    if (!password || password.length < 6) return false;
    if (password !== confirmPassword) return false;
    if (!roleId) return false;
    if (!isCaregiver && !(task?.email || '').trim()) return false;
    if (isAdminWeakPassword) return false;
    return true;
  }, [username, password, confirmPassword, roleId, isCaregiver, task, isAdminWeakPassword]);

  const handleApprove = useCallback(() => {
    if (!isFormValid || !task) return;
    onApprove({
      display_name: task.display_name || '',
      email: task.email || '',
      phone: (task.phone || '').replace(/-/g, ''),
      username: username.trim(),
      password,
      user_role_id: parseInt(roleId, 10),
      title: title === 'Other' ? titleCustom.trim() : title,
      group_ids: selectedGroupIds,
      reports: [],
      cloud_id: task.cloud_id,
    });
  }, [isFormValid, task, username, password, roleId, title, titleCustom, selectedGroupIds, onApprove]);

  if (!task) return null;

  return (
    <div className="dp-drawer-backdrop" onClick={onClose} aria-hidden="true">
      <div
        className="pp-dialog pp-dialog--wide"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label="Approve Add User"
      >
        {/* Header */}
        <div className="pp-dialog__header">
          <h3 className="pp-dialog__title">Approve Add User</h3>
          <button
            type="button"
            onClick={onClose}
            className="dp-drawer__close"
            aria-label="Close dialog"
          >
            <svg className="dp-icon-lg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Body */}
        <div className="pp-dialog__body">
          {/* Read-only EHR-supplied fields */}
          <div className="ehr-dialog__field-row">
            <div className="ehr-dialog__field">
              <span className="ehr-dialog__field-label">Display Name</span>
              <span className="ehr-dialog__field-value">{task.display_name || '\u2014'}</span>
            </div>
          </div>
          <div className="ehr-dialog__field-row">
            <div className="ehr-dialog__field">
              <span className="ehr-dialog__field-label">Email</span>
              <span className="ehr-dialog__field-value">{task.email || '\u2014'}</span>
            </div>
            <div className="ehr-dialog__field">
              <span className="ehr-dialog__field-label">Phone</span>
              <span className="ehr-dialog__field-value">{task.phone || '\u2014'}</span>
            </div>
          </div>

          <hr className="ehr-dialog__divider" />

          {/* Editable form fields */}
          <div className="ehr-dialog__field-row">
            <div className="ehr-dialog__field">
              <label htmlFor="ehr-user-username" className="up-field__label">
                Username <span className="up-field__required">*</span>
              </label>
              <input
                type="text"
                id="ehr-user-username"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                pattern="^[a-zA-Z0-9.\-_@]+$"
                className="up-field__input"
                placeholder="Enter username"
              />
            </div>
            <div className="ehr-dialog__field">
              <label htmlFor="ehr-user-role" className="up-field__label">
                Role <span className="up-field__required">*</span>
              </label>
              <select
                id="ehr-user-role"
                value={roleId}
                onChange={(e) => setRoleId(e.target.value)}
                className="up-field__input"
              >
                <option value="">Select role...</option>
                {filteredRoles.map((r) => (
                  <option key={r.user_role_id} value={r.user_role_id}>
                    {r.user_role_name}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="ehr-dialog__field-row">
            <div className="ehr-dialog__field">
              <label htmlFor="ehr-user-title" className="up-field__label">Title</label>
              <select
                id="ehr-user-title"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                className="up-field__input"
              >
                {TITLE_OPTIONS.map((opt) => (
                  <option key={opt || 'none'} value={opt}>
                    {opt || 'Select title...'}
                  </option>
                ))}
              </select>
              {title === 'Other' ? (
                <input
                  type="text"
                  value={titleCustom}
                  onChange={(e) => setTitleCustom(e.target.value)}
                  placeholder="Enter custom title"
                  className="up-field__input"
                  style={{ marginTop: 'var(--space-2)' }}
                />
              ) : null}
            </div>
          </div>

          <div className="ehr-dialog__field-row">
            <div className="ehr-dialog__field">
              <label htmlFor="ehr-user-pw" className="up-field__label">
                Password <span className="up-field__required">*</span>
              </label>
              <input
                type="password"
                id="ehr-user-pw"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="up-field__input"
                placeholder="At least 6 characters"
                autoComplete="new-password"
              />
            </div>
            <div className="ehr-dialog__field">
              <label htmlFor="ehr-user-pw-confirm" className="up-field__label">
                Confirm Password <span className="up-field__required">*</span>
              </label>
              <input
                type="password"
                id="ehr-user-pw-confirm"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="up-field__input"
                placeholder="Re-enter password"
                autoComplete="new-password"
              />
            </div>
          </div>
          {password && confirmPassword && password !== confirmPassword ? (
            <p className="up-field__hint" style={{ color: 'var(--color-danger)' }}>
              Passwords do not match.
            </p>
          ) : null}
          {isAdminWeakPassword ? (
            <p className="up-field__hint" style={{ color: 'var(--color-danger)' }}>
              Admin role requires a stronger password.
            </p>
          ) : null}

          <hr className="ehr-dialog__divider" />

          {/* Groups picker */}
          <div className="ehr-group-picker">
            <div className="ehr-group-picker__header">
              <h4 className="ehr-group-picker__title">Groups</h4>
              <div className="ehr-group-picker__controls">
                <div className="dp-search" style={{ flex: '1' }}>
                  <svg className="dp-icon-md dp-search__icon" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" aria-hidden="true">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z" />
                  </svg>
                  <input
                    type="search"
                    placeholder="Search groups..."
                    value={groupSearch}
                    onChange={(e) => setGroupSearch(e.target.value)}
                    className="dp-search__input"
                    aria-label="Search groups"
                  />
                </div>
              </div>
            </div>

            <div className="ehr-group-picker__scroll">
              <table className="dp-table">
                <thead className="dp-thead">
                  <tr>
                    <th scope="col" className="dp-th dp-th--checkbox" />
                    <th scope="col" className="dp-th">Group</th>
                    <th scope="col" className="dp-th">Capcode</th>
                  </tr>
                </thead>
                <tbody className="dp-tbody">
                  {filteredGroups.map((g) => {
                    const id = String(g.group_id);
                    const checked = selectedGroupIds.includes(id);
                    return (
                      <tr
                        key={g.group_id}
                        className={`dp-tr ${checked ? 'is-selected' : ''}`}
                        onClick={() => handleGroupToggle(g.group_id)}
                        style={{ cursor: 'pointer' }}
                      >
                        <td className="dp-td dp-td--checkbox">
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={() => handleGroupToggle(g.group_id)}
                            className="dp-checkbox"
                            aria-label={`Select ${g.group_name}`}
                            onClick={(e) => e.stopPropagation()}
                          />
                        </td>
                        <td className="dp-td">
                          <span className="dp-cell-text">{g.group_name}</span>
                        </td>
                        <td className="dp-td">
                          <span className="dp-cell-text">{g.capcode || '\u2014'}</span>
                        </td>
                      </tr>
                    );
                  })}
                  {filteredGroups.length === 0 ? (
                    <tr className="dp-tr">
                      <td className="dp-td dp-td--center" colSpan={3} style={{ padding: '1rem' }}>
                        <span className="dp-cell-text--muted">No groups found</span>
                      </td>
                    </tr>
                  ) : null}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="pp-dialog__footer pp-dialog__footer--split">
          <div className="ehr-dialog__footer-left">
            <button
              type="button"
              onClick={onReject}
              className="dp-btn dp-btn--danger-outline"
              disabled={submitting}
            >
              Reject
            </button>
          </div>
          <div className="ehr-dialog__footer-right">
            <button
              type="button"
              onClick={onClose}
              className="dp-btn dp-btn--secondary"
              disabled={submitting}
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleApprove}
              disabled={submitting || !isFormValid}
              className="dp-btn dp-btn--primary"
            >
              {submitting ? 'Approving...' : 'Approve'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ApproveAddUserDialog;
